import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddTwoToneIcon from "@mui/icons-material/PersonAddTwoTone";
import TuneTwoToneIcon from "@mui/icons-material/TuneTwoTone";
import type {
  FilterState,
  FilterOperator,
  User,
  Tag,
  Task,
  RoomStatus,
  CustomColumn,
  CustomColumnCondition,
} from "../types/types.ts";
import { MemberSelectionDialog } from "./MemberSelectionDialog.tsx";
import { isoToYmd, ymdToUtcIso } from "../utils/date.ts";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";

interface Props {
  onClose: () => void;
  onApplyFilters: (filters: FilterState) => void;
  initialFilters: Partial<FilterState>;
  roomMembers: User[];
  availableTags: Tag[];
  color: PaletteColor;
  roomStatuses: RoomStatus[];
  roomId: number;
  activeListId?: string | null;
  honbuId?: number;
  editingMyFilter?: {
    filterId: number;
    filterName: string;
    filterConditions: string;
  } | null;
  onSaveMyFilter?: (name: string, conditions: FilterState) => void;
  onUpdateMyFilter?: (
    id: number,
    name: string,
    conditions: FilterState
  ) => void;
  onDeleteMyFilter?: (id: number) => void;
}

const OperatorToggle = ({
  value,
  onChange,
  name,
}: {
  value: FilterOperator;
  onChange: (op: FilterOperator) => void;
  name: string;
}) => (
  <div className="flex items-center space-x-2">
    <label className="text-xs flex items-center">
      <input
        type="radio"
        name={`op-${name}`}
        checked={value === "OR"}
        onChange={() => onChange("OR")}
        className="mr-1"
      />
      または
    </label>
    <label className="text-xs flex items-center">
      <input
        type="radio"
        name={`op-${name}`}
        checked={value === "AND"}
        onChange={() => onChange("AND")}
        className="mr-1"
      />
      かつ
    </label>
  </div>
);

const SaveMyFilterDialog = ({
  onSave,
  onCancel,
  myFilterName,
  setMyFilterName,
  color,
}: {
  onSave: () => void;
  onCancel: () => void;
  myFilterName: string;
  setMyFilterName: (name: string) => void;
  color: PaletteColor;
}) => (
  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-20">
    <div className="bg-white p-6 rounded-lg shadow-xl space-y-4 w-11/12 max-w-sm">
      <h4 className="font-bold">Myフィルターに名前を付けて保存</h4>
      <input
        type="text"
        value={myFilterName}
        onChange={(e) => setMyFilterName(e.target.value)}
        placeholder="フィルター名を入力"
        className="w-full p-2 border rounded"
      />
      <div className="flex justify-end space-x-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 border rounded hover:bg-gray-100"
        >
          キャンセル
        </button>
        <button
          onClick={onSave}
          className="px-4 py-2 text-white rounded"
          style={{
            backgroundColor: PaletteColorHex[color],
            backgroundImage:
              "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
            boxShadow:
              "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
          }}
        >
          登録
        </button>
      </div>
    </div>
  </div>
);

const completeInitialFilters: FilterState = {
  statuses: [],
  startDate: "",
  endDate: "",
  assigneeIds: [],
  assigneeOperator: "OR",
  requesterIds: [],
  requesterOperator: "OR",
  tagIds: [],
  tagOperator: "OR",
  priorities: [],
  starredOnly: false,
  myTasksOnly: false,
  myRequestsOnly: false,
  dueTodayOnly: false,
  showCompleted: false,
  showHidden: false,
  searchKeyword: "",
  searchMode: "name",
  customColumnConditions: [],
};

const priorityStyles: Record<Task["priority"], string> = {
  高: "text-red-500 font-semibold",
  中: "text-orange-500 font-semibold",
  低: "text-blue-500 font-semibold",
  なし: "text-gray-600",
};

export function FilterDialog({
  onClose,
  onApplyFilters,
  initialFilters,
  roomMembers,
  availableTags,
  color,
  roomStatuses,
  roomId,
  activeListId,
  honbuId,
  editingMyFilter,
  onSaveMyFilter,
  onUpdateMyFilter,
  onDeleteMyFilter,
}: Props) {
  const isMyList = roomId === 0;
  const [filters, setFilters] = useState<FilterState>({
    ...completeInitialFilters,
    ...initialFilters,
    startDate: initialFilters.startDate
      ? isoToYmd(initialFilters.startDate)
      : "",
    endDate: initialFilters.endDate ? isoToYmd(initialFilters.endDate) : "",
    customColumnConditions: initialFilters.customColumnConditions ?? [],
  });

  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);

  useEffect(() => {
    if (!activeListId || activeListId === "0") {
      setCustomColumns([]);
      return;
    }
    fetch(`/api/todolists/${activeListId}/custom-columns?honbuId=${honbuId ?? 0}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data: CustomColumn[]) => setCustomColumns(data))
      .catch(() => setCustomColumns([]));
  }, [activeListId, honbuId]);

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [myFilterName, setMyFilterName] = useState(
    editingMyFilter?.filterName || ""
  );
  const [showMemberDialog, setShowMemberDialog] = useState<
    "assignees" | "requesters" | null
  >(null);
  const [newCondColumnId, setNewCondColumnId] = useState<number | "">("");
  const [newCondValue, setNewCondValue] = useState("");

  const primaryButtonStyle = {
    backgroundColor: PaletteColorHex[color],
    color: "white",
    backgroundImage:
      "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
    boxShadow:
      "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
  };

  const handleStatusChange = (status: Task["status"]) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    setFilters((f) => ({ ...f, statuses: newStatuses }));
  };

  const handlePriorityChange = (priority: Task["priority"]) => {
    const newPriorities = filters.priorities.includes(priority)
      ? filters.priorities.filter((p) => p !== priority)
      : [...filters.priorities, priority];
    setFilters((f) => ({ ...f, priorities: newPriorities }));
  };

  const handleTagChange = (tagId: number) => {
    const newTagIds = filters.tagIds.includes(tagId)
      ? filters.tagIds.filter((id) => id !== tagId)
      : [...filters.tagIds, tagId];
    setFilters((f) => ({ ...f, tagIds: newTagIds }));
  };

  const handleMemberSave = (selectedNames: string[]) => {
    if (!showMemberDialog) return;
    const selectedIds = selectedNames
      .map((name) => roomMembers.find((m) => m.name === name)?.id)
      .filter((id): id is string => !!id);

    if (showMemberDialog === "assignees") {
      setFilters((f) => ({ ...f, assigneeIds: selectedIds }));
    } else {
      setFilters((f) => ({ ...f, requesterIds: selectedIds }));
    }
    setShowMemberDialog(null);
  };

  const handleReset = () => {
    setFilters(completeInitialFilters);
    setNewCondColumnId("");
    setNewCondValue("");
  };

  const addCustomColumnCondition = () => {
    if (newCondColumnId === "") return;
    const col = customColumns.find((c) => c.customColumnId === newCondColumnId);
    if (!col) return;
    if (col.columnType === "text" && !newCondValue.trim()) return;
    if (col.columnType === "select" && !newCondValue) return;
    const cond: CustomColumnCondition = {
      id: `${Date.now()}-${Math.random()}`,
      columnId: col.customColumnId,
      columnName: col.columnName,
      columnType: col.columnType,
      value: newCondValue.trim(),
    };
    setFilters((f) => ({
      ...f,
      customColumnConditions: [...(f.customColumnConditions ?? []), cond],
    }));
    setNewCondValue("");
  };

  const removeCustomColumnCondition = (id: string) => {
    setFilters((f) => ({
      ...f,
      customColumnConditions: (f.customColumnConditions ?? []).filter(
        (c) => c.id !== id
      ),
    }));
  };

  const buildOutgoingFilters = (): FilterState => {
    return {
      ...filters,
      startDate: filters.startDate ? ymdToUtcIso(filters.startDate) : "",
      endDate: filters.endDate ? ymdToUtcIso(filters.endDate) : "",
    };
  };

  const handleSaveMyFilterClick = () => {
    if (!myFilterName) {
      alert("Myフィルター名を入力してください。");
      return;
    }
    const outgoing = buildOutgoingFilters();
    onSaveMyFilter?.(myFilterName, outgoing);
    setShowSaveDialog(false);
  };

  const handleSubmit = () => {
    const outgoing = buildOutgoingFilters();
    if (editingMyFilter) {
      if (!myFilterName) {
        alert("Myフィルター名を入力してください。");
        return;
      }
      onUpdateMyFilter?.(editingMyFilter.filterId, myFilterName, outgoing);
    } else {
      onApplyFilters(outgoing);
    }
  };

  // フィルタートグルUI
  const FilterToggle = ({
    label,
    checked,
    onChange,
  }: {
    label: string;
    checked: boolean;
    onChange: () => void;
  }) => (
    <label className="flex items-center justify-between text-sm cursor-pointer py-2">
      <span>{label}</span>
      <div
        className={`w-11 h-6 rounded-full transition-colors ${
          checked ? "bg-green-500" : "bg-gray-300"
        }`}
        onClick={onChange}
      >
        <div
          className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          } mt-0.5`}
        />
      </div>
    </label>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 sm:p-4"
      onClick={onClose}
    >
      {showMemberDialog && (
        <MemberSelectionDialog
          roomMembers={roomMembers}
          initialSelected={(showMemberDialog === "assignees"
            ? filters.assigneeIds
            : filters.requesterIds
          )
            .map((id) => roomMembers.find((m) => m.id === id.toString())?.name)
            .filter((name): name is string => !!name)}
          onSave={handleMemberSave}
          onClose={() => setShowMemberDialog(null)}
          dialogType={showMemberDialog}
        />
      )}
      <div
        className="bg-gray-50 w-full h-full sm:rounded-xl sm:max-w-md sm:max-h-[90vh] flex flex-col shadow-2xl border-2 border-white overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {showSaveDialog && (
          <SaveMyFilterDialog
            myFilterName={myFilterName}
            setMyFilterName={setMyFilterName}
            onSave={handleSaveMyFilterClick}
            onCancel={() => setShowSaveDialog(false)}
            color={color}
          />
        )}
        <div
          className="flex justify-between items-center px-4 py-3 flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${PaletteColorHex[color]}, ${PaletteColorHex[color]}CC)`,
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <TuneTwoToneIcon fontSize="small" />
            <span className="font-bold text-sm">絞り込み</span>
          </div>
          <div className="flex items-center space-x-3">
            {onSaveMyFilter &&
              (editingMyFilter ? (
                <button
                  onClick={() => onDeleteMyFilter?.(editingMyFilter.filterId)}
                  className="px-3 py-1.5 text-sm text-white bg-white/20 rounded-lg hover:bg-white/30 transition"
                >
                  Myフィルターを削除
                </button>
              ) : (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="px-3 py-1.5 text-sm text-white bg-white/20 rounded-lg hover:bg-white/30 transition"
                >
                  Myフィルターに保存
                </button>
              ))}
            <button
              onClick={onClose}
              className="p-1 rounded-full text-white hover:bg-white/20"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          {editingMyFilter && (
            <div className="bg-white p-4 rounded-lg border">
              <label
                htmlFor="my-filter-name"
                className="font-semibold text-sm mb-2 block"
              >
                Myフィルター名
              </label>
              <input
                id="my-filter-name"
                type="text"
                value={myFilterName}
                onChange={(e) => setMyFilterName(e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
          )}

          <div className="bg-white p-4 rounded-lg border block sm:hidden">
            <h5 className="font-semibold text-sm mb-2">表示設定</h5>
            <div className="space-y-1">
              <FilterToggle
                label="スターつきタスクのみ表示"
                checked={filters.starredOnly}
                onChange={() =>
                  setFilters((f) => ({ ...f, starredOnly: !f.starredOnly }))
                }
              />
              <FilterToggle
                label="完了タスクを表示"
                checked={filters.showCompleted}
                onChange={() =>
                  setFilters((f) => ({ ...f, showCompleted: !f.showCompleted }))
                }
              />
              <FilterToggle
                label="非表示タスクを表示"
                checked={filters.showHidden}
                onChange={() =>
                  setFilters((f) => ({ ...f, showHidden: !f.showHidden }))
                }
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <h5 className="font-semibold text-sm mb-2">ステータスで絞り込む</h5>
            <div className="grid grid-cols-2 gap-2">
              {roomStatuses.map((status) => (
                <label
                  key={status.roomStatusId}
                  className="flex items-center space-x-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filters.statuses.includes(
                      status.statusName as Task["status"]
                    )}
                    onChange={() =>
                      handleStatusChange(status.statusName as Task["status"])
                    }
                  />
                  <span
                    style={{
                      color: PaletteColorHex[status.color],
                      fontWeight: 500,
                    }}
                  >
                    {status.statusName}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <h5 className="font-semibold text-sm mb-2">期限で絞り込む</h5>
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((f) => {
                    const start = e.target.value;
                    let end = f.endDate;
                    if (end && start && end < start) end = start;
                    return { ...f, startDate: start, endDate: end };
                  })
                }
                className="w-full p-2 border rounded"
              />
              <span>～</span>
              <input
                type="date"
                value={filters.endDate}
                min={filters.startDate || undefined}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, endDate: e.target.value }))
                }
                className="w-full p-2 border rounded"
              />
            </div>
            {filters.startDate &&
              filters.endDate &&
              filters.endDate < filters.startDate && (
                <p className="mt-1 text-xs text-red-500">
                  終了日は開始日以降を指定してください
                </p>
              )}
          </div>

          {!isMyList && (
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex justify-between items-center mb-2">
                <h5 className="font-semibold text-sm">
                  担当者で絞り込み ({filters.assigneeIds.length}名)
                </h5>
                <OperatorToggle
                  name="assignee"
                  value={filters.assigneeOperator}
                  onChange={(op) =>
                    setFilters((f) => ({ ...f, assigneeOperator: op }))
                  }
                />
              </div>
              <div
                className="flex items-center flex-wrap gap-1 mt-2 p-2 border rounded min-h-[40px] cursor-pointer"
                onClick={() => setShowMemberDialog("assignees")}
              >
                {filters.assigneeIds.map((id) => {
                  const member = roomMembers.find(
                    (m) => m.id === id.toString()
                  );
                  return member ? (
                    <span
                      key={id}
                      className="text-xs px-2 py-1 bg-gray-200 rounded-full"
                    >
                      {member.name}
                    </span>
                  ) : null;
                })}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMemberDialog("assignees");
                  }}
                  className="text-sm hover:underline ml-auto"
                  style={{ color: PaletteColorHex[color] }}
                >
                  <PersonAddTwoToneIcon
                    fontSize="small"
                    className="mb-0.5 mr-1"
                  />
                  追加
                </button>
              </div>
            </div>
          )}

          {!isMyList && (
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex justify-between items-center mb-2">
                <h5 className="font-semibold text-sm">
                  依頼者で絞り込み ({filters.requesterIds.length}名)
                </h5>
                <OperatorToggle
                  name="requester"
                  value={filters.requesterOperator}
                  onChange={(op) =>
                    setFilters((f) => ({ ...f, requesterOperator: op }))
                  }
                />
              </div>
              <div
                className="flex items-center flex-wrap gap-1 mt-2 p-2 border rounded min-h-[40px] cursor-pointer"
                onClick={() => setShowMemberDialog("requesters")}
              >
                {filters.requesterIds.map((id) => {
                  const member = roomMembers.find(
                    (m) => m.id === id.toString()
                  );
                  return member ? (
                    <span
                      key={id}
                      className="text-xs px-2 py-1 bg-gray-200 rounded-full"
                    >
                      {member.name}
                    </span>
                  ) : null;
                })}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMemberDialog("requesters");
                  }}
                  className="text-sm hover:underline ml-auto"
                  style={{ color: PaletteColorHex[color] }}
                >
                  <PersonAddTwoToneIcon
                    fontSize="small"
                    className="mb-0.5 mr-1"
                  />
                  追加
                </button>
              </div>
            </div>
          )}

          <div className="bg-white p-4 rounded-lg border">
            <h5 className="font-semibold text-sm mb-2">重要度で絞り込む</h5>
            <div className="grid grid-cols-2 gap-2">
              {(["高", "中", "低", "なし"] as const).map((p) => (
                <label
                  key={p}
                  className="flex items-center space-x-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filters.priorities.includes(p)}
                    onChange={() => handlePriorityChange(p)}
                  />
                  <span className={priorityStyles[p]}>{p}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-semibold text-sm">タグで絞り込む</h5>
              <OperatorToggle
                name="tag"
                value={filters.tagOperator}
                onChange={(op) =>
                  setFilters((f) => ({ ...f, tagOperator: op }))
                }
              />
            </div>
            <div className="max-h-24 overflow-y-auto flex flex-wrap gap-2">
              {availableTags.map((tag) => {
                const isSelected = filters.tagIds.includes(tag.id);
                const hexColor = tag.color || PaletteColorHex.Blue;
                return (
                  <button
                    key={tag.id}
                    onClick={() => handleTagChange(tag.id)}
                    style={{
                      backgroundColor: isSelected ? `${hexColor}20` : "white",
                      color: isSelected ? hexColor : `${hexColor}A0`,
                      border: `1px solid ${hexColor}A0`,
                    }}
                    className="px-3 py-1 rounded-full text-xs font-semibold transition-colors"
                  >
                    #{tag.name}
                  </button>
                );
              })}
            </div>
          </div>

          {customColumns.length > 0 && (
            <div className="bg-white p-4 rounded-lg border">
              <h5 className="font-semibold text-sm mb-1">
                カスタム項目で絞り込む
              </h5>
              <p className="text-xs text-gray-400 mb-3">
                同じ項目: いずれか一致 / 異なる項目: すべて一致
              </p>

              {(filters.customColumnConditions ?? []).length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {(filters.customColumnConditions ?? []).map((cond) => (
                    <div
                      key={cond.id}
                      className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1.5"
                    >
                      <span className="text-xs font-medium text-gray-600 shrink-0 border border-gray-200 rounded px-1.5 py-0.5">
                        {cond.columnName}
                      </span>
                      <span className="text-xs flex-1 truncate">
                        {cond.value}
                      </span>
                      <button
                        onClick={() => removeCustomColumnCondition(cond.id)}
                        className="text-gray-400 hover:text-red-500 shrink-0 leading-none"
                      >
                        <CloseIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <select
                  value={newCondColumnId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewCondColumnId(val === "" ? "" : Number(val));
                    setNewCondValue("");
                  }}
                  className="text-xs border rounded px-2 py-1.5 shrink-0 bg-white max-w-[40%]"
                >
                  <option value="">項目を選択</option>
                  {customColumns.map((col) => (
                    <option key={col.customColumnId} value={col.customColumnId}>
                      {col.columnName}
                    </option>
                  ))}
                </select>

                {newCondColumnId !== "" && (() => {
                  const col = customColumns.find(
                    (c) => c.customColumnId === newCondColumnId
                  );
                  if (!col) return null;
                  if (col.columnType === "select") {
                    return (
                      <select
                        value={newCondValue}
                        onChange={(e) => setNewCondValue(e.target.value)}
                        className="flex-1 text-xs border rounded px-2 py-1.5 min-w-0 bg-white"
                      >
                        <option value="">値を選択</option>
                        {col.options
                          .slice()
                          .sort((a, b) => a.displayOrder - b.displayOrder)
                          .map((opt) => (
                            <option key={opt.optionId} value={opt.optionValue}>
                              {opt.optionValue}
                            </option>
                          ))}
                      </select>
                    );
                  }
                  return (
                    <input
                      type="text"
                      value={newCondValue}
                      onChange={(e) => setNewCondValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addCustomColumnCondition();
                      }}
                      placeholder="検索ワードを入力"
                      className="flex-1 text-xs border rounded px-2 py-1.5 min-w-0"
                    />
                  );
                })()}

                <button
                  onClick={addCustomColumnCondition}
                  disabled={
                    newCondColumnId === "" ||
                    !newCondValue.trim()
                  }
                  className="text-xs px-3 py-1.5 rounded shrink-0 disabled:opacity-40 transition-opacity"
                  style={primaryButtonStyle}
                >
                  追加
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 mt-auto border-t flex space-x-2 bg-white sm:rounded-b-lg">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2 border rounded hover:bg-gray-100 transition"
          >
            リセット
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 rounded-lg transition"
            style={primaryButtonStyle}
          >
            {editingMyFilter ? "変更を保存" : "この条件で絞り込む"}
          </button>
        </div>
      </div>
    </div>
  );
}
